import moment from 'moment-timezone';
import { IST_ZONE } from './ist_time';

/**
 * Turns an activity row into a sentence. The API stores values in canonical machine
 * format so entries stay comparable, which makes formatting the feed's job.
 *
 * Times render in IST — the host entered them in IST and could not recognise their
 * own change in UTC. A row carries only the entity's uuid, so `lookup` resolves it
 * against the runs the detail screen already holds.
 */

const DATE_FORMAT = 'ddd D MMM YYYY';
const TIME_FORMAT = 'h:mm A';

/** An ISO 8601 UTC instant, shown as an IST wall clock. */
const asISTTime = (iso) => (iso ? moment(iso).tz(IST_ZONE).format(TIME_FORMAT) : null);

/** An ISO date (`2026-08-15`) has no instant, so it is read by its calendar fields. */
const asDate = (isoDate) => (isoDate ? moment(isoDate, 'YYYY-MM-DD').format(DATE_FORMAT) : null);

/** Indexes runs and slots by uuid so a log row can name what it changed. */
export const buildLookup = (runs) => {
  const byUUID = {};

  (runs || []).forEach((run) => {
    byUUID[run.run_uuid] = { kind: 'run', date: run.run_date };
    (run.slots || []).forEach((slot) => {
      byUUID[slot.slot_uuid] = { kind: 'slot', start_utc: slot.start_utc, run_date: run.run_date };
    });
  });

  return byUUID;
};

/** "the run Sat 15 Aug 2026", or a bare noun when the uuid is unknown to us. */
const describeEntity = (row, lookup) => {
  const found = lookup[row.entity_uuid];

  if (row.entity_type === 'run') {
    return found?.date ? `the run ${asDate(found.date)}` : 'the run';
  }
  if (row.entity_type === 'slot') {
    return found?.start_utc ? `the ${asISTTime(found.start_utc)} slot` : 'a slot';
  }
  return 'the event';
};

/**
 * Field-specific phrasing for `action = updated`. Returns null when the field is one
 * this function doesn't know, so the caller can fall back rather than render a
 * half-sentence.
 */
const describeFieldChange = (row, lookup) => {
  const { field, old_value: oldValue, new_value: newValue } = row;

  switch (field) {
    case 'name':
      return `renamed the event from ${oldValue} to ${newValue}`;

    case 'run_date':
      return `moved ${describeEntity(row, lookup)} to ${asDate(newValue)}`;

    case 'duration_mins':
      return `set duration to ${newValue} min on ${describeEntity(row, lookup)}`;

    case 'start_utc':
      return `changed slot time from ${asISTTime(oldValue)} to ${asISTTime(newValue)}`;

    // old_value is null when a link is set for the first time, and new_value is null
    // when one is cleared. Three distinct events, not one.
    case 'meeting_link':
      if (!oldValue) return `added the meeting link for ${describeEntity(row, lookup)}`;
      if (!newValue) return `removed the meeting link for ${describeEntity(row, lookup)}`;
      return `updated the meeting link for ${describeEntity(row, lookup)}`;

    default:
      return null;
  }
};

/**
 * describeActivity returns the sentence following the actor's name, so the feed reads
 * "Eikshit changed slot time from 3:00 PM to 5:00 PM".
 */
export const describeActivity = (row, lookup = {}) => {
  if (row.action === 'updated') {
    return describeFieldChange(row, lookup) || `updated ${describeEntity(row, lookup)}`;
  }

  const entity = describeEntity(row, lookup);

  switch (row.action) {
    case 'created':
      return `created ${entity}`;
    case 'published':
      return `published ${entity}`;
    // A run cancel writes one row; the slot cancellations it triggers are implied by
    // it and are deliberately not logged, so this never reads as four entries.
    case 'cancelled':
      return `cancelled ${entity}`;
    case 'duplicated':
      return `duplicated ${entity}`;
    // The run is gone, so entity_uuid resolves to nothing and `entity` is a bare
    // noun. old_value is the only surviving record of which date it was.
    case 'deleted':
      return row.old_value ? `deleted the run ${asDate(row.old_value)}` : `deleted ${entity}`;
    default:
      return `changed ${entity}`;
  }
};

/** "2 min ago". The absolute time goes in a title attribute for anyone who needs it. */
export const relativeTime = (iso) => moment(iso).fromNow();

/** The exact instant, in IST, for the hover title. */
export const absoluteTime = (iso) => moment(iso).tz(IST_ZONE).format('D MMM YYYY, h:mm A [IST]');

/** The actor's display name, falling back to the email the log row denormalises. */
export const actorName = (row) => row.actor_name || row.actor_email || 'Someone';
